import * as React from "react";
import { Box } from "../../components/ui/box";
import { Icon } from "../../components/ui/icon";
import { Input } from "../../components/ui/input";
import { useEntitySelector } from "./entity-selector-context";
import type { Omittable } from "../../lib/props";

export type EntitySelectorInputProps = Omit<
  React.ComponentProps<typeof Input>,
  "value" | "onChange" | "unstyled" | "bare" | "className" | "style"
> & Omittable<{
  placeholder?: string;
  autoFocus?: boolean;
  /** Hide the leading search glyph (shown by default). */
  hideIcon?: boolean;
  ref?: React.Ref<HTMLInputElement>;
}>;

/**
 * The flush search row pinned at the top of the selector list: only a bottom
 * border, no clear/spinner — a header divider, not a standalone field. Query
 * state lives in the root; this renders it and writes back on input.
 */
function EntitySelectorInput({
  placeholder,
  autoFocus = true,
  hideIcon = false,
  ref: forwardedRef,
  ...props
}: EntitySelectorInputProps) {
  const { search, setSearch, config, inputRef } = useEntitySelector();

  const mergedRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef, inputRef],
  );

  const resolvedPlaceholder =
    placeholder ?? config.searchPlaceholder ?? "Search...";

  return (
    // The wrapper's fixed height is the row's one vertical metric: glyph and
    // input both center against it, so the two can never sit on different
    // baselines.
    <Box
      direction="row"
      align="center"
      gap="sm"
      paddingX="sm"
      border="bottom"
      shrink={false}
      className="h-9"
    >
      {/* Glyph at the menu-item scale so the header reads as a row of the
          same list, not an oversized standalone field. */}
      {!hideIcon && <Icon name="search" size="sm" color="muted" />}
      {/* `bare`: no shell span, no fixed input metrics — the wrapper's h-9
          is the only vertical metric. No vertical padding either, so the
          browser centers the single text line inside `h-full`. */}
      <Input
        bare
        ref={mergedRef}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={resolvedPlaceholder}
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
        role="searchbox"
        // text-base below sm: a mobile text control under 16px triggers iOS
        // Safari's focus zoom (the same guard the Input primitive carries).
        className="h-full min-w-0 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm"
        {...props}
      />
    </Box>
  );
}

EntitySelectorInput.displayName = "EntitySelectorInput";

export { EntitySelectorInput };
